<template>
  <v-row class="mt-3 mb-5 d-flex justify-end">
    <v-btn color="primary" prepend-icon="mdi-plus" @click="goCreateTerminalForm()">
      {{ $t("page.terminalForm.addTerminalForm") }}
    </v-btn>
  </v-row>

  <v-card class="pa-6">
    <!-- terminal form data table with firebase pagination -->
    <v-data-table
      :headers="headers"
      :items="forms"
      item-value="id"
      hover
      :loading="useTerminalFormsStore().loading || loading"
      items-per-page="10"
      class="terminal-form-table"
      @click:row="handleRowClick"
    >
      <template v-slot:[`item.submissionCount`]="{ item }">
        <v-chip size="small" color="primary" variant="tonal" class="submission-count-chip">
          {{ submissionCountsMap[item.id] ?? "—" }}
        </v-chip>
      </template>
      <template v-slot:[`item.status`]="{ item }">
        <status-chip
          class="mr-6"
          :status="item.archived ? TerminalFormStatus.ARCHIVED : TerminalFormStatus.ACTIVE"
        />
      </template>
      <template v-slot:[`item.createdAt`]="{ item }">
        <p class="text-grey">
          {{ formatCreatedAt(item?.createdAt) }}
        </p>
      </template>
      <template v-slot:[`item.menu`]="{ item }">
        <v-menu>
          <template v-slot:activator="{ props }">
            <v-btn v-bind="props" icon flat>
              <v-icon color="grey">mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-for="(action, index) in item.archived ? inactiveActionList : activeActionList"
              :key="index"
              :value="index"
              @click.stop="action.action(item.id, item.archived)"
              :class="{
                'dotted-line-below':
                  activeActionList[index + 1] &&
                  activeActionList[index + 1].title === $t('common.action.archive'),
                'text-red': action.title === $t('common.action.archive'),
                'text-green': action.title === $t('common.action.activate'),
              }"
            >
              <v-list-item-title>
                {{ action.title }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:bottom="{}">
        <v-divider />
        <v-layout class="pr-2 py-1">
          <span />
          <v-spacer />
          <v-btn
            icon="mdi-chevron-left"
            class="mr-3"
            variant="plain"
            @click="previousPage()"
            :disabled="loading || disabledPrev"
          />
          <v-btn
            icon="mdi-chevron-right"
            variant="plain"
            @click="nextPage()"
            :disabled="loading || disabledNext"
          />
        </v-layout>
      </template> </v-data-table
  ></v-card>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useTerminalFormsStore } from "@/terminal-forms/stores/terminalForms";
import { getTimeFormattedWithAMAndPM } from "@/shared/utils/date.utils";
import StatusChip from "@/shared/components/Common/StatusChip.vue";
import { TerminalFormStatus } from "@/shared/enums/status.enum";
import i18n from "@/app/plugins/i18n";
import { useRouter } from "vue-router";
import { useAccountStore } from "@/account/stores/account";
import { db } from "@/app/plugins/firebase";
import {
  collection,
  getCountFromServer,
  onSnapshot,
  query,
  where,
  type DocumentData,
  type Query,
  type Unsubscribe,
} from "firebase/firestore";
import type { TerminalForm } from "@/terminal-forms/types/terminalForm";

interface TerminalFormTableHeader {
  title: string;
  align: "start" | "center" | "end";
  value: string;
  sortable: boolean;
}

const router = useRouter();
const accountStore = useAccountStore();
const forms = computed(() => useTerminalFormsStore().forms);
const loading = ref(false);
const disabledNext = computed(() => !useTerminalFormsStore().hasMore);
const prev = computed(() => useTerminalFormsStore().previousPage);
const next = computed(() => useTerminalFormsStore().nextPage);
const firstPageId = ref("");
const submissionCounts = ref<Record<string, number>>({});
const accountId = computed(() => accountStore.account?.id ?? null);
const submissionCountsMap = computed(() => submissionCounts.value);

// createdAt is an ISO timestamp from the terminal-forms API; convert to unix
// seconds before formatting.
const formatCreatedAt = (createdAt: string | undefined): string => {
  if (!createdAt) {
    return "-";
  }
  const parsed = Date.parse(createdAt);
  if (Number.isNaN(parsed)) {
    return "-";
  }
  return getTimeFormattedWithAMAndPM(Math.floor(parsed / 1000));
};

const countListeners = new Map<string, Unsubscribe>();
const countQueries = new Map<string, Query<DocumentData>>();
const countTokens = new Map<string, number>();

onMounted(async () => {
  await getFormList();
  if (forms.value.length) {
    firstPageId.value = forms.value[0].id;
  }
});

const headers: TerminalFormTableHeader[] = [
  {
    title: i18n.global.t("common.action.name"),
    align: "start",
    sortable: true,
    value: "name",
  },
  {
    title: i18n.global.t("page.terminalForm.submissionCount"),
    align: "center",
    value: "submissionCount",
    sortable: false,
  },
  {
    title: i18n.global.t("common.action.status"),
    align: "center",
    value: "status",
    sortable: true,
  },
  {
    title: i18n.global.t("common.action.dateCreated"),
    align: "center",
    value: "createdAt",
    sortable: true,
  },
  {
    title: i18n.global.t("common.action.actions"),
    align: "center",
    value: "menu",
    sortable: false,
  },
];

const getFormList = async () => {
  try {
    loading.value = true;
    await useTerminalFormsStore().listTerminalForms();
  } finally {
    loading.value = false;
  }
};
const inactiveActionList = [
  {
    title: i18n.global.t("common.action.edit"),
    action: (id: string) => {
      router.push({ path: `/terminalForms/${id}` });
    },
  },
  {
    title: i18n.global.t("common.action.activate"),
    action: async (id: string, status: boolean) => {
      await useTerminalFormsStore().updateTerminalFormStatus(!status, id);
      await getFormList();
    },
  },
];

const activeActionList = [
  {
    title: i18n.global.t("common.action.edit"),
    action: (id: string) => {
      router.push({ path: `/terminalForms/${id}` });
    },
  },
  {
    title: i18n.global.t("common.action.archive"),
    action: async (id: string, status: boolean) => {
      await useTerminalFormsStore().updateTerminalFormStatus(!status, id);
      await getFormList();
    },
  },
];

const previousPage = async () => {
  useTerminalFormsStore().setStartAfter(null);
  useTerminalFormsStore().setEndBefore(prev.value);
  await getFormList();
};

const nextPage = async () => {
  useTerminalFormsStore().setEndBefore(null);
  useTerminalFormsStore().setStartAfter(next.value);
  await getFormList();
};

const disabledPrev = computed(
  () => !useTerminalFormsStore().previousPage || forms.value[0]?.id === firstPageId.value,
);

const goCreateTerminalForm = () => {
  router.push({ name: "terminalFormsCreate" });
};

const handleRowClick = (_event: Event, context: { item: TerminalForm }) => {
  const id = context?.item?.id;
  if (!id) {
    return;
  }
  router.push({ name: "terminalFormsDetail", params: { id } });
};

const subscribeToSubmissionCount = (formId: string) => {
  if (!accountId.value) {
    return;
  }

  const submissionsRef = collection(db, "accounts", accountId.value, "formCompleted");
  const formQuery = query(submissionsRef, where("formId", "==", formId));
  countQueries.set(formId, formQuery);

  const unsubscribe = onSnapshot(formQuery, () => refreshSubmissionCount(formId));
  countListeners.set(formId, unsubscribe);
};

const refreshSubmissionCount = async (formId: string) => {
  const formQuery = countQueries.get(formId);
  if (!formQuery) {
    return;
  }

  const token = (countTokens.get(formId) ?? 0) + 1;
  countTokens.set(formId, token);
  try {
    const snapshot = await getCountFromServer(formQuery);
    if (countTokens.get(formId) !== token) {
      return;
    }
    const countValue = snapshot.data().count ?? 0;
    submissionCounts.value = {
      ...submissionCounts.value,
      [formId]: countValue,
    };
  } catch (error) {
    console.error(`[TerminalFormListCard] Failed to count submissions for form ${formId}`, error);
  }
};

const detachSubmissionListener = (formId: string) => {
  const unsubscribe = countListeners.get(formId);
  if (unsubscribe) {
    unsubscribe();
    countListeners.delete(formId);
  }
  countQueries.delete(formId);
  countTokens.delete(formId);
  const updated = { ...submissionCounts.value };
  delete updated[formId];
  submissionCounts.value = updated;
};

const detachAllSubmissionListeners = () => {
  countListeners.forEach((unsubscribe) => unsubscribe());
  countListeners.clear();
  countQueries.clear();
  countTokens.clear();
  submissionCounts.value = {};
};

watch(
  [forms, accountId],
  ([currentForms, currentAccountId]) => {
    if (!currentAccountId) {
      detachAllSubmissionListeners();
      return;
    }

    const currentIds = new Set(
      currentForms
        .map((form) => form?.id)
        .filter((id: string | undefined): id is string => Boolean(id)),
    );

    Array.from(countListeners.keys()).forEach((formId) => {
      if (!currentIds.has(formId)) {
        detachSubmissionListener(formId);
      }
    });

    currentIds.forEach((formId) => {
      if (!countListeners.has(formId)) {
        subscribeToSubmissionCount(formId);
      }
    });
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  detachAllSubmissionListeners();
});
</script>
<style>
.dotted-line-below {
  border-bottom: 1px dotted #ccc;
  padding-bottom: 8px;
  margin-bottom: 8px;
}
.text-red {
  color: red;
}

.delete-action {
  border-top: none !important;
}
.currency-uppercase {
  text-transform: uppercase;
}
.terminal-form-table :deep(tbody tr) {
  cursor: pointer;
}
.submission-count-chip {
  min-width: 48px;
  justify-content: center;
}
</style>
